import { useState } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, DatePicker, Popconfirm, message, Space, Tag } from "antd";
import { Plus } from "lucide-react";
import dayjs from "dayjs";
import { useInsurances, useInsuranceCreate, useInsuranceUpdate, useInsuranceDelete } from "../../../../api-hooks/compliance";

export default function InsurancesTab({ cid }: { cid: number }) {
  const [msgApi, ctx] = message.useMessage();
  const [modal, setModal] = useState<{ open: boolean; rec: any }>({ open: false, rec: null });
  const [form] = Form.useForm();

  const { data: insurances = [], isLoading } = useInsurances(cid);
  const cb = (msg: string) => ({ onSuccess: () => { msgApi.success(msg); setModal({ open: false, rec: null }); }, onError: (e: any) => msgApi.error(e?.message || "Error") });
  const create = useInsuranceCreate(cid, cb("Insurance added."));
  const update = useInsuranceUpdate(cid, cb("Updated."));
  const remove = useInsuranceDelete(cid, { onSuccess: () => msgApi.success("Deleted."), onError: (e: any) => msgApi.error(e?.message) });

  function openEdit(r?: any) {
    setModal({ open: true, rec: r ?? null });
    form.setFieldsValue(r ? { ...r, start_date: r.start_date ? dayjs(r.start_date) : null, expiry_date: r.expiry_date ? dayjs(r.expiry_date) : null } : { reminder_days: 30, is_active: 1 });
  }

  const isExpired = (expiry: string) => expiry && dayjs(expiry).isBefore(dayjs());
  const isExpiringSoon = (expiry: string) => expiry && !isExpired(expiry) && dayjs(expiry).diff(dayjs(), "day") <= 30;

  const columns = [
    { title: "Type", dataIndex: "insurance_type", key: "t" },
    { title: "Provider", dataIndex: "provider", key: "p", render: (v: string) => v || "—" },
    { title: "Policy #", dataIndex: "policy_number", key: "pn", render: (v: string) => v || "—" },
    { title: "Cover (£)", dataIndex: "cover_amount", key: "c", render: (v: number) => v ? `£${Number(v).toLocaleString()}` : "—" },
    { title: "Expiry", dataIndex: "expiry_date", key: "e", render: (v: string) => {
      if (!v) return "—";
      const expired = isExpired(v); const soon = isExpiringSoon(v);
      return <Tag color={expired ? "red" : soon ? "orange" : "green"}>{dayjs(v).format("DD/MM/YY")}{expired ? " EXPIRED" : soon ? " SOON" : ""}</Tag>;
    }},
    { title: "", key: "act", render: (_: any, r: any) => (
      <Space size="small">
        <Button size="small" onClick={() => openEdit(r)}>Edit</Button>
        <Popconfirm title="Delete?" onConfirm={() => remove.mutate({ id: r.id })} okButtonProps={{ danger: true }}><Button size="small" danger>Del</Button></Popconfirm>
      </Space>
    )},
  ];

  return (
    <div>
      {ctx}
      <div className="flex justify-between mb-3">
        <h3 className="font-semibold text-slate-700 text-sm">Company Insurances</h3>
        <Button size="small" type="primary" icon={<Plus size={13} />} onClick={() => openEdit()} style={{ background: "#4c1d95" }}>Add Insurance</Button>
      </div>
      <Table rowKey="id" dataSource={insurances as any[]} columns={columns} loading={isLoading} size="small" pagination={false} />
      <Modal open={modal.open} onCancel={() => setModal({ open: false, rec: null })} footer={null} destroyOnClose title={modal.rec ? "Edit Insurance" : "New Insurance"} width={480}>
        <Form form={form} layout="vertical" size="middle" className="pt-2" onFinish={(vals) => {
          const p = { ...vals, start_date: vals.start_date?.format("YYYY-MM-DD"), expiry_date: vals.expiry_date?.format("YYYY-MM-DD") };
          if (modal.rec) update.mutate({ id: modal.rec.id, ...p }); else create.mutate(p);
        }}>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="insurance_type" label="Insurance Type" rules={[{ required: true }]}><Input placeholder="e.g. Public Liability" /></Form.Item>
            <Form.Item name="provider" label="Provider"><Input /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="policy_number" label="Policy Number"><Input /></Form.Item>
            <Form.Item name="cover_amount" label="Cover Amount (£)"><InputNumber className="w-full" min={0} /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
            <Form.Item name="expiry_date" label="Expiry Date" rules={[{ required: true }]}><DatePicker className="w-full" format="DD/MM/YYYY" /></Form.Item>
          </div>
          <div className="grid grid-cols-2 gap-x-3">
            <Form.Item name="reminder_days" label="Reminder (days before)"><InputNumber className="w-full" min={1} /></Form.Item>
            <Form.Item name="document_url" label="Document URL"><Input /></Form.Item>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="primary" htmlType="submit" style={{ background: "#4c1d95" }} loading={create.isPending || update.isPending}>Save</Button>
            <Button onClick={() => setModal({ open: false, rec: null })}>Cancel</Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
